import { computed, ref, watch } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { IBTWBAccount, IEvent, IEventProfile, IEventRegistration } from './eventTypes'
import { useCustomerStore } from '@/js/stores/customerStore'
import type { IRogue } from '@/js/services/RogueTypes'

const defaultProfileState: IEventProfile = {
  loaded    : false,
  linked    : false,
  _id       : '',
  firstName : '',
  lastName  : '',
  email     : '',
  btwbId    : null,
  birthdate : '',
  gym       : '',
  gender    : 'Male',
  country   : '',
}

export const useEventStore = defineStore('events', () => {
  const customerStore = useCustomerStore()

  const profile = ref<IEventProfile>(Object.assign({}, defaultProfileState))
  const registrations = ref<IEventRegistration[]>([])
  const events = ref<{ [sku: string]: IEvent }>({})

  // rogueObj is set in loadProfile. Used with profileLoaded to reload the profile if someone logs out and back in after loadProfile was initially called.
  let rogueObj: IRogue | undefined
  let profileLoaded = false

  const loggedIn = computed<boolean>(() => customerStore.loggedIn)

  function $reset() {
    profile.value = Object.assign({}, defaultProfileState)
    if (loggedIn.value) {
      profile.value._id = customerStore._id || defaultProfileState._id
      profile.value.firstName = customerStore.firstname
      profile.value.lastName = customerStore.lastname
      profile.value.email = customerStore.email || defaultProfileState.email
    }
    registrations.value = []
  }

  $reset()

  function updateProfile(data: object) {
    if (!loggedIn.value) {
      return
    }

    Object.assign(profile.value, data)
  }

  async function ensureCustomerForProfile(rogue: IRogue) {
    if (!profile.value._id) {
      await customerStore.init(rogue)
      await customerStore.loadCustomer(rogue)
      $reset()
    }
  }

  async function fetchAccountFromSvc(rogue: IRogue) {
    const resp = await rogue.fetch<{ account?: Partial<IBTWBAccount>; linked: boolean; registrations: IEventRegistration[] }>('/svc_event/account')

    registrations.value = (resp.registrations || []).filter(r => r.divisionId !== undefined)

    if (resp.account) {
      updateProfile(resp.account)
    }

    profile.value.linked = resp.linked
    profile.value.loaded = true
    profileLoaded = true
  }

  /**
   * Loads the event profile from `/svc_event/account`.
   * @param rogue - The Rogue instance
   * @param force - If true, refetches even if already loaded (e.g. mobile webview after desktop changes)
   */
  async function loadProfile(rogue: IRogue, force = false) {
    rogueObj = rogue
    if (!force && (profile.value.loaded || !loggedIn.value)) {
      return
    }
    if (!loggedIn.value) {
      return
    }

    await ensureCustomerForProfile(rogue)

    try {
      await fetchAccountFromSvc(rogue)
    } catch (e) {
      rogue.log.warn('Error loading btwb', e as Error)
    }
  }

  watch(loggedIn, (newLoggedInValue) => {
    $reset()
    if (newLoggedInValue && profileLoaded && rogueObj) {
      loadProfile(rogueObj)
    }
  })

  const pendingEventConfigs = new Map<string, Promise<void>>()

  async function loadEventConfig(sku: string, rogue: IRogue) {
    if (events.value[sku]) {
      return
    }

    const pending = pendingEventConfigs.get(sku)
    if (pending) {
      return pending
    }

    const request = (async () => {
      try {
        const eventConfig = await rogue.fetch<IEvent>(`/svc_event/events/${sku}`)
        events.value[sku] = eventConfig
      } finally {
        pendingEventConfigs.delete(sku)
      }
    })()
    pendingEventConfigs.set(sku, request)

    return request
  }

  function setEventConfigs(list: IEvent[]) {
    const latest: { [sku: string]: IEvent } = {}
    for (const event of list) {
      if (!event?.sku) {
        continue
      }
      const current = latest[event.sku]
      if (!current || (event.season || '') >= (current.season || '')) {
        latest[event.sku] = event
      }
    }
    Object.assign(events.value, latest)
  }

  async function saveProfile(rogue: IRogue, link = false) {
    if (!loggedIn.value) {
      return
    }

    const toSave = JSON.parse(JSON.stringify(profile.value))

    if (!toSave.birthdate) {
      delete toSave.birthdate
    }
    if (!toSave.gym) {
      delete toSave.gym
    }
    if (!toSave.country) {
      delete toSave.country
    }

    const data = await rogue.fetch<IBTWBAccount>('/svc_event/account', 'put', toSave)

    updateProfile(data)
    if (!profile.value.linked && link) {
      profile.value.linked = true
      rogue.track.track({ type: 'linkBtwb' })
    }
  }

  return {
    profile,
    registrations,
    events,
    updateProfile,
    loadProfile,
    loadEventConfig,
    setEventConfigs,
    saveProfile,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot))
}
